import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import { withTaskRecovery } from 'af-root/context/taskRecoveryContext';
import { useTaskRecovery } from 'af-root/hooks/useTaskRecovery';
import DisplayReviewStatusLabel from 'af-root/scenes/Company/FieldReports/Shared/DisplayReviewStatusLabel';

import type { W_ReportType_FindReportTypeViewTable_VM_Row } from 'ab-api/web/workOrderReport/reportTypeViewTable';

import type FieldReportTypePreviewInModalVM from 'ab-viewModels/fieldReport/fieldReportTypeSidePanel.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import * as TimeUtils from '@acceligentllc/shared/utils/time';
import { isCalculatedReportDisplayStatusOfInterestForRole } from '@acceligentllc/shared/utils/workOrderReport';

import { FieldReportAccessRole } from '@acceligentllc/shared/enums/fieldReportAccessRole';
import type { CalculatedReportDisplayStatus } from '@acceligentllc/shared/enums/reportDisplayStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';
import { TimeSheetApprovalStatus } from '@acceligentllc/shared/enums/timeSheetApprovalStatus';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import { debounce } from 'af-utils/actions.util';
import { downloadCSV } from 'af-utils/csv.utils';
import { getSegmentCount } from 'af-utils/fieldReport.util';
import { useLazyLoad, useLazyLoadWithInvalidation } from 'af-utils/react.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';
import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import CLIENT from 'af-constants/routes/client';

import { DEFAULT_TABLE_PAGE_SIZE, SEARCH_DELAY } from 'ab-common/constants/value';
import type { TableQuery } from 'ab-common/dataStructures/tableQuery';
import DisabledFeatures from 'ab-common/environment/disabledFeatures';

import TableNew from 'af-components/Table';
import type { TableRef } from 'af-components/Table';
import FilterInput from 'af-components/Table/ActionHeader/FilterInput';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import LinkCell from 'af-components/Table/Cells/LinkCell';
import TextCell from 'af-components/Table/Cells/TextCell';
import AddFilter from 'af-components/Table/Filters/AddFilter';
import SelectToLoad from 'af-components/Table/States/SelectToLoad';
import type { TableProps } from 'af-components/Table/types';

import { findAllForReportTypeView } from 'af-actions/accounts';
import { findAllForOrderReportFilter } from 'af-actions/equipment';
import * as FieldReportActions from 'af-actions/fieldReport';
import * as JobActions from 'af-actions/jobs';
import * as ReportTypeActions from 'af-actions/reportType';

import AdditionalFilters from './AdditionalFilters';
import { useReportTypeViewAdditionalFields } from './AdditionalFilters/customHooks';
import ReportTypeViewTableDateFilter from './DateFilter';
import ExportReportTypesModal from './ExportReportTypesModal';
import { toCSVData } from './helpers';
import { JobFilterItem } from './JobFilterItem';
import ReportTypeFilter from './ReportTypeFilter';
import { ReportTypeFilterItem } from './ReportTypeFilter/types';
import ReportTypeViewBulkFilterModal from './ReportTypeViewBulkFilterModal';
import FieldReportTypeSidePanel from './SidePanel';
import FieldReportTypeSidePanelHeader from './SidePanel/Header';
import styles from './styles.module.scss';
import { ADDITIONAL_OPTIONS } from './types';
import type { ReportTypeViewTableAdditionalFilter } from './types';
import SelectWorkOrdersModal from '../../FieldReportsList/FieldReport/Modals/SelectWorkOrdersModal';

const ALLOWED_RETRIES = 4;
interface SettingProps {
	startDate: Date;
	endDate: Date;
	period: TimePeriodRecurrence;
	reportTypeId: string;
	jobId: string;
	textFilter: string;
}

const connector = connect(mapStateToProps, mapDispatchToProps());

type Props = SettingProps & ConnectedProps<typeof connector>;

const sortableColumns = ['reportTypeName', 'workOrderCode', 'companyName', 'startDate', 'calculatedReportDisplayStatus', 'reportTypeStatus', ''] as const;
type SortableColumn = (typeof sortableColumns)[number];
function isSortableColumn(value: string): value is SortableColumn {
	return sortableColumns.includes(value as SortableColumn);
}

const isTimeSheetRejected = (timeSheet: W_ReportType_FindReportTypeViewTable_VM_Row['timeSheets'][0]) => timeSheet.approvalStatus === TimeSheetApprovalStatus.REJECTED;

const hasCurrentAccountTimeSheetRejected = (currentAcc: Nullable<number>) => (_ts) => isTimeSheetRejected(_ts) && _ts.accountId === currentAcc;

const _filterEmptyAdditionalFilters = (_filter: ReportTypeViewTableAdditionalFilter) => _filter.value !== null;

const ReportTypeViewTable = (props: Props) => {
	const {
		companyData,
		isAccountingOrAdmin,
		isManagement,
		findAllForReportTypeViewTable,
		loadJobs,
		loadReportTypes,
		findFieldReportTypeForSidePanel,
		findOrCreateGroup,
		downloadFieldReportTypePDF,
		loadReportBlockFieldsForFilter,
		findAllAccountsForCompany,
		findAllEquipmentForCompany,
	} = props;

	const location = useLocation();
	const orgAlias = location.state.orgAlias;
	const taskRecovery = useTaskRecovery();

	const tableRef = React.useRef<TableRef<W_ReportType_FindReportTypeViewTable_VM_Row>>(null);
	const [startDate, setStartDate] = React.useState(TimeUtils.positionDate(new Date(), 'start', 'day'));
	const [endDate, setEndDate] = React.useState(TimeUtils.positionDate(new Date(), 'end', 'day'));
	const [period, setPeriod] = React.useState(TimePeriodRecurrence.DAILY);
	const [filterByReportType, setFilterByReportType] = React.useState<Nullable<ReportTypeFilterItem>>(null);
	const [searchText, setSearchText] = React.useState('');
	const [debouncedSearchText, setDebouncedSearchText] = React.useState('');

	const [fieldReportTypeToPreview, setFieldReportTypeToPreview] = React.useState<Nullable<FieldReportTypePreviewInModalVM>>(null);
	const [showFieldReportTypeModal, setShowFieldReportTypeModal] = React.useState(false);
	const [showExportReportTypesModal, setShowExportReportTypesModal] = React.useState(false);
	const [showBulkFilterModal, setShowBulkFilterModal] = React.useState(false);
	const [currentlyPreviewedRow, setCurrentlyPreviewedRow] = React.useState<Nullable<Row<W_ReportType_FindReportTypeViewTable_VM_Row>>>(null);
	const [currentlyPreviewedImage, setCurrentlyPreviewedImage] = React.useState<Nullable<string>>(null);
	const [FRTypeForSelectWOsModal, setFRTypeForSelectWOsModal] = React.useState<Nullable<W_ReportType_FindReportTypeViewTable_VM_Row>>(null);

	const {
		resetAdditionalFilters,
		editFilter,
		additionalFilters,
		addAdditionalFilter,
		removeFilter,
		bulkEditAdditionalFilters,
		completeAdditionalFilters,
	} = useReportTypeViewAdditionalFields();

	const loadAndSetReportTypes = React.useCallback(async () => {
		const fetchedReportTypes = await loadReportTypes();
		return ReportTypeFilterItem.bulkConstructor(fetchedReportTypes);
	}, [loadReportTypes]);

	const loadReportBlockFieldOptions = React.useCallback(async () => {
		if (!filterByReportType) {
			return [];
		}

		const newOptions = await loadReportBlockFieldsForFilter(
			filterByReportType.id,
			TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY, TimeFormat.ISO_DATETIME),
			TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY, TimeFormat.ISO_DATETIME)
		);
		return newOptions;
	}, [endDate, filterByReportType, loadReportBlockFieldsForFilter, startDate]);

	const loadAndSetJobs = React.useCallback(async () => {
		const fetchedJobs = await loadJobs();

		return JobFilterItem.bulkConstructor(fetchedJobs);
	}, [loadJobs]);

	const loadAndSetEquipment = React.useCallback(async () => {
		const fetchedEquipment = await findAllEquipmentForCompany(
			TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY, TimeFormat.ISO_DATETIME),
			TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY, TimeFormat.ISO_DATETIME)
		);

		return fetchedEquipment.equipments
			.map((_equipment) => ({ id: _equipment.id, code: _equipment.code, specification: _equipment.specification }));
	}, [findAllEquipmentForCompany, startDate, endDate]);

	const { options: reportTypeOptions, lazyLoad: lazyLoadReportTypes } = useLazyLoad(loadAndSetReportTypes);
	const { options: equipmentOptions, lazyLoad: lazyLoadEquipment } = useLazyLoad(loadAndSetEquipment);
	const { options: accountOptions, lazyLoad: lazyLoadAccounts } = useLazyLoad(findAllAccountsForCompany);
	const { options: jobOptions, lazyLoad: lazyLoadJobs } = useLazyLoad(loadAndSetJobs);

	const { options: reportBlockFieldOptions, lazyLoad: lazyLoadReportBlockFields } = useLazyLoadWithInvalidation(
		loadReportBlockFieldOptions,
		[endDate, filterByReportType, startDate, period]
	);

	const changeReportType = React.useCallback(async (newReportType: Nullable<ReportTypeFilterItem>) => {
		setFilterByReportType(newReportType);
		resetAdditionalFilters();
	}, [resetAdditionalFilters]);

	const closeFieldReportTypeModal = React.useCallback(() => setShowFieldReportTypeModal(false), []);
	const openFieldReportTypeModal = React.useCallback(() => setShowFieldReportTypeModal(true), []);
	const closeSelectWOsModal = React.useCallback(() => setFRTypeForSelectWOsModal(null), []);

	const fetchData = React.useCallback(async (tableRequestModel: TableQuery) => {
		const { page, pageSize, sortBy } = tableRequestModel;

		const { id, desc } = sortBy?.length ? { ...sortBy[0] } : { id: '', desc: false };
		const sort: SortableColumn = isSortableColumn(id) ? id : '';

		if (!filterByReportType) {
			return undefined;
		}

		const rows = await findAllForReportTypeViewTable(
			startDate,
			endDate,
			page,
			pageSize ?? DEFAULT_TABLE_PAGE_SIZE,
			debouncedSearchText ?? '',
			sort,
			!desc,
			filterByReportType.id,
			completeAdditionalFilters
		);

		return rows;
	}, [completeAdditionalFilters, debouncedSearchText, endDate, filterByReportType, findAllForReportTypeViewTable, startDate]);

	React.useEffect(() => {
		if (tableRef.current) {
			tableRef.current.refreshTable();
		}
	}, [
		startDate,
		endDate,
		period,
		debouncedSearchText,
		filterByReportType,
		completeAdditionalFilters,
	]);

	const onOpenBulkModalFilterClick = React.useCallback(() => {
		setShowBulkFilterModal(true);
	}, []);

	const onCloseBulkModalFilterClick = React.useCallback(() => {
		setShowBulkFilterModal(false);
	}, []);

	const debouncedSetSearch = React.useMemo(
		() => debounce((value: string) => {
			setDebouncedSearchText(value);
		}, SEARCH_DELAY),
		[]
	);

	const onFilterTextChange = React.useCallback((_filter: string) => {
		setSearchText(_filter); // Update immediately for UI responsiveness
		debouncedSetSearch(_filter);
	}, [debouncedSetSearch]);

	const renderFilter = React.useCallback(() => {
		return (
			<>
				<FilterInput
					filterText={searchText}
					onFilterTextChange={onFilterTextChange}
					openBulkFilter={onOpenBulkModalFilterClick}
				/>
				<ReportTypeFilter
					defaultValue={filterByReportType}
					lazyLoadReportTypes={lazyLoadReportTypes}
					reportTypeOptions={reportTypeOptions}
					setFilterByReportType={changeReportType}
				/>
				<ReportTypeViewTableDateFilter
					endDate={endDate}
					fiscalYearStartMonth={companyData.fiscalYearStartMonth}
					period={period}
					setEndDate={setEndDate}
					setPeriod={setPeriod}
					setStartDate={setStartDate}
					startDate={startDate}
				/>
				{filterByReportType && (
					<AddFilter
						addAdditionalFilter={addAdditionalFilter}
						options={ADDITIONAL_OPTIONS}
					/>
				)}
			</>
		);
	},
		[
			searchText,
			onOpenBulkModalFilterClick,
			lazyLoadReportTypes,
			reportTypeOptions,
			changeReportType,
			endDate,
			period,
			startDate,
			filterByReportType,
			addAdditionalFilter,
			onFilterTextChange,
			companyData.fiscalYearStartMonth,
		]);

	const renderDateCell = React.useCallback(() => (cell) => {

		const dbDate = cell.getValue() as string;

		const date = TimeUtils.formatDate(dbDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);

		return <TextCell value={date} />;
	}, []);

	const renderTextCell = React.useCallback(() => (cell) => {
		return <TextCell value={cell.getValue()} />;
	}, []);

	const renderLinkCell = React.useCallback(() =>
		(_cell: CellContext<W_ReportType_FindReportTypeViewTable_VM_Row, string>) => {
			if (!_cell.cell.row.original.workOrderCode) {
				return (
					<TextCell className={styles['canceled-work-order-status-cell']} value="N/A" />
				);
			}

			const path = _cell.row.original.workOrderId
				? CLIENT.COMPANY.FIELD_REPORT.ALL_REPORTS(_cell.row.original.workOrderId.toString(), orgAlias, companyData.name)
				: '';

			return (
				<LinkCell
					label={_cell.row.original.workOrderCode!}
					path={path}
				/>
			);
		}, [companyData.name, orgAlias]);

	const renderStatusCell = React.useCallback(() =>
		(_cell: CellContext<W_ReportType_FindReportTypeViewTable_VM_Row, Nullable<CalculatedReportDisplayStatus>>) => {
			if (_cell.row.original.workOrderStatus === WorkOrderStatus.CANCELED) {
				return <TextCell value="N/A" />;
			}
			let role = _cell.row.original.fieldReportAccessRole;
			if (isManagement) {
				role = FieldReportAccessRole.MANAGEMENT;
			}
			if (isAccountingOrAdmin) {
				role = FieldReportAccessRole.ACCOUNTING;
			}

			const hasCurrentUserRejectedTimeSheet = _cell.row.original.timeSheets.some(hasCurrentAccountTimeSheetRejected(companyData.accountId));

			let isOfInterest = false;
			if (role) {
				isOfInterest = isCalculatedReportDisplayStatusOfInterestForRole(
					role,
					_cell.row.original.calculatedReportDisplayStatus,
					companyData.accountId,
					_cell.row.original.assignedToIds,
					hasCurrentUserRejectedTimeSheet
				) ?? false;
			}

			return (
				<DisplayReviewStatusLabel
					calculatedReportDisplayStatus={_cell.getValue()}
					isCondensedView={false}
					isOfInterest={isOfInterest}
				/>
			);
		}, [companyData.accountId, isAccountingOrAdmin, isManagement]);

	const onPreviewActionClick = React.useCallback((row: Row<W_ReportType_FindReportTypeViewTable_VM_Row>) => () => {
		setCurrentlyPreviewedRow(row);
		openFieldReportTypeModal();
	}, [openFieldReportTypeModal]);

	const onTableRowClick = React.useCallback((row: Row<W_ReportType_FindReportTypeViewTable_VM_Row>) => {
		setCurrentlyPreviewedRow(row);
	}, []);

	const onExportAsPDFClick = React.useCallback((fieldReportTypeIds: number[], isForCustomer: boolean, reportName: string) => async () => {
		const groupCode = await findOrCreateGroup(fieldReportTypeIds, isForCustomer);
		if (!!groupCode) {
			await downloadFieldReportTypePDF(groupCode, reportName, taskRecovery, ALLOWED_RETRIES, isForCustomer);
		}
		// notificationSnackbar and taskRecovery not here since
		// it would make new resolveActionsButton function with every tick, which would then cause table rerender
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [downloadFieldReportTypePDF, findOrCreateGroup]);

	const onAttachToWorkOrderClick = React.useCallback((fieldReportType: W_ReportType_FindReportTypeViewTable_VM_Row) => async () => {
		setFRTypeForSelectWOsModal(fieldReportType);
	}, []);

	const resolveActionsButton = React.useCallback((_cell: CellContext<W_ReportType_FindReportTypeViewTable_VM_Row, unknown>) => {
		const actions = [
			{ onClick: onPreviewActionClick(_cell.row), label: 'Preview' },
		];

		if (_cell.row.original.workOrderStatus !== WorkOrderStatus.CANCELED) {
			actions.push({ onClick: onExportAsPDFClick([_cell.row.original.id], true, `${_cell.row.original.reportTypeName} ${_cell.row.original.workOrderCode}`), label: 'Export for Customer' });
			actions.push({ onClick: onExportAsPDFClick([_cell.row.original.id], false, `${_cell.row.original.reportTypeName} ${_cell.row.original.workOrderCode}`), label: 'Export Complete Report' });

			if (!DisabledFeatures.PreviousFieldReports) {
				actions.push({ onClick: onAttachToWorkOrderClick(_cell.row.original), label: 'Attach to Work Order' });
			}
		}

		return (
			<ActionsCell
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={actions}
				valueKey="label"
			/>
		);
	}, [onAttachToWorkOrderClick, onExportAsPDFClick, onPreviewActionClick]);

	const columns: TableProps<W_ReportType_FindReportTypeViewTable_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'reportTypeName',
			header: 'Report Type',
			accessor: 'reportTypeName',
			cell: renderTextCell(),
			enableSorting: true,
		},
		{
			id: 'workOrderCode',
			header: 'Field Report',
			accessor: 'workOrderCode',
			cell: renderLinkCell(),
			enableSorting: true,
		},
		{
			id: 'companyName',
			header: 'Company',
			accessor: 'companyName',
			cell: renderTextCell(),
			enableSorting: true,
		},
		{
			id: 'startDate',
			header: 'Start Date',
			accessor: 'startDate',
			cell: renderDateCell(),
			enableSorting: true,
		},
		{
			id: 'calculatedReportDisplayStatus',
			header: 'FR Status',
			accessor: 'calculatedReportDisplayStatus',
			cell: renderStatusCell(),
			rightAligned: false,
			enableSorting: true,
		},
		{
			id: 'reportTypeStatus',
			header: 'RT Status',
			accessor: 'isContainingData',
			cell: (_cell: CellContext<W_ReportType_FindReportTypeViewTable_VM_Row, boolean>) => <TextCell value={_cell.row.original.isContainingData ? 'Containing data' : 'Empty'} />,
			enableSorting: true,
		},
		{
			id: 'actions',
			isDisplayColumn: true,
			header: () => <EmptyCell isHeader />,
			cell: resolveActionsButton,
		},
	], [renderDateCell, renderLinkCell, renderStatusCell, renderTextCell, resolveActionsButton]);

	const [segmentCountMap, instanceCount] = React.useMemo(() => {
		const _segmentCountMap = getSegmentCount(fieldReportTypeToPreview?.fieldReportBlocks ?? []);
		const _instanceCount = Object.keys(_segmentCountMap).length;
		return [_segmentCountMap, _instanceCount];
	}, [fieldReportTypeToPreview?.fieldReportBlocks]);

	const renderSidePanelContext = React.useCallback((selectedRow: Row<W_ReportType_FindReportTypeViewTable_VM_Row>) => {
		return (
			<FieldReportTypeSidePanel
				fieldReportId={selectedRow.original.fieldReportId}
				fieldReportTypeId={selectedRow.original.id}
				fieldReportTypeToPreview={fieldReportTypeToPreview}
				findFieldReportTypeForSidePanel={findFieldReportTypeForSidePanel}
				instanceCount={instanceCount}
				segmentCountMap={segmentCountMap}
				setFieldReportTypeToPreview={setFieldReportTypeToPreview}
			/>
		);
	}, [fieldReportTypeToPreview, findFieldReportTypeForSidePanel, instanceCount, segmentCountMap]);

	const closeImagePreviewModal = React.useCallback(() => {
		setCurrentlyPreviewedImage(null);
		setShowFieldReportTypeModal(true);
	}, []);

	const onImageClick = React.useCallback((imageSrc: string) => {
		setShowFieldReportTypeModal(false);
		setCurrentlyPreviewedImage(imageSrc);
	}, []);

	const renderSidePanelHeader = React.useCallback((selectedRow: Row<W_ReportType_FindReportTypeViewTable_VM_Row>) => {
		const headerValue = `${selectedRow.original.reportTypeName} ${selectedRow.original.workOrderCode}`;
		return (
			<FieldReportTypeSidePanelHeader
				closeFieldReportTypeModal={closeFieldReportTypeModal}
				closeImagePreviewModal={closeImagePreviewModal}
				currentlyPreviewedImage={currentlyPreviewedImage}
				fieldReportTypeToPreview={fieldReportTypeToPreview}
				headerValue={headerValue}
				instanceCount={instanceCount}
				onExportAsPDFClick={onExportAsPDFClick}
				onImageClick={onImageClick}
				openFieldReportTypeModal={openFieldReportTypeModal}
				segmentCountMap={segmentCountMap}
				showFieldReportTypeModal={showFieldReportTypeModal}
			/>
		);
	}, [closeFieldReportTypeModal,
		fieldReportTypeToPreview,
		instanceCount,
		onExportAsPDFClick,
		openFieldReportTypeModal,
		segmentCountMap,
		showFieldReportTypeModal,
		currentlyPreviewedImage,
		closeImagePreviewModal,
		onImageClick,
	]);

	const emptyTableMessage = React.useMemo(() => {
		if (!filterByReportType) {
			return (
				<SelectToLoad name="Report Type" />
			);
		}

		return undefined;
	}, [filterByReportType]);

	const onExportReportTypesClick = React.useCallback(async () => {
		setShowExportReportTypesModal(true);
	}, []);

	const onExportTableClick = React.useCallback(async () => {
		const rows = tableRef.current?.getTableData();
		if (rows?.length) {
			downloadCSV(toCSVData(rows), 'report_type_table.csv');
		}
	}, []);

	const closeExportReportTypesModal = React.useCallback(async () => {
		setShowExportReportTypesModal(false);
	}, []);

	const buttons = React.useMemo(() => [
		{ label: 'Export Table', icon: 'ActionDownload', type: TableButtonType.LINK, hasPermission: true, onClick: onExportTableClick },
		{ label: 'Export Report Types (PDF)', icon: 'ActionDownload', type: TableButtonType.LINK, hasPermission: true, onClick: onExportReportTypesClick },
	], [onExportReportTypesClick, onExportTableClick]);

	const bulkEditTableFilters = React.useCallback((
		_reportType: ReportTypeFilterItem,
		_startDate: Date,
		_endDate: Date,
		_period: TimePeriodRecurrence,
		_search: string,
		_additionalFilters: ReportTypeViewTableAdditionalFilter[]
	) => {
		setFilterByReportType(_reportType);
		setStartDate(_startDate);
		setEndDate(_endDate);
		setPeriod(_period);
		bulkEditAdditionalFilters(_additionalFilters);
		setSearchText(_search);
		setDebouncedSearchText(_search);
	}, [bulkEditAdditionalFilters]);

	const renderAdditionalFilters = React.useCallback(() => {
		if (!additionalFilters.length) {
			return null;
		}

		return (
			<AdditionalFilters
				accountOptions={accountOptions.users}
				editFilter={editFilter}
				equipmentOptions={equipmentOptions}
				filters={additionalFilters}
				jobOptions={jobOptions}
				lazyLoadAccountOptions={lazyLoadAccounts}
				lazyLoadEquipmentOptions={lazyLoadEquipment}
				lazyLoadJobOptions={lazyLoadJobs}
				lazyLoadReportBlockFieldsOptions={lazyLoadReportBlockFields}
				removeFilter={removeFilter}
				reportBlockFieldOptions={reportBlockFieldOptions}
				resetAdditionalFilters={resetAdditionalFilters}
			/>
		);
	},
		[
			accountOptions,
			additionalFilters,
			lazyLoadReportBlockFields,
			editFilter,
			equipmentOptions,
			lazyLoadAccounts,
			lazyLoadEquipment,
			removeFilter,
			reportBlockFieldOptions,
			resetAdditionalFilters,
			jobOptions,
			lazyLoadJobs,
		]);

	return (
		<>
			<TableNew
				additionalFilter={renderFilter}
				buttons={buttons}
				columns={columns}
				currentlyPreviewedSidePanelRow={currentlyPreviewedRow}
				emptyTableMessage={emptyTableMessage}
				fetch={fetchData}
				filtersClassName={styles['table-filters']}
				hasSearchInput={false}
				offsetHeight={10}
				onRowClick={onTableRowClick}
				openBulkFilter={onOpenBulkModalFilterClick}
				ref={tableRef}
				renderSidePanelContext={renderSidePanelContext}
				renderSidePanelHeader={renderSidePanelHeader}
				sidePanelVisible={true}
				specializedFilters={renderAdditionalFilters}
				tableName={TableNameEnum.REPORT_TYPE_VIEW_TABLE}
			/>
			{showExportReportTypesModal && (
				<ExportReportTypesModal
					closeModal={closeExportReportTypesModal}
					fetchData={findAllForReportTypeViewTable}
					fiscalYearStartMonth={companyData.fiscalYearStartMonth}
					loadReportTypes={loadReportTypes}
					onExportAsPDFClick={onExportAsPDFClick}
					selectedEndDate={endDate}
					selectedPeriod={period}
					selectedReportType={filterByReportType}
					selectedStartDate={startDate}
					showModal={showExportReportTypesModal}
				/>
			)}
			{showBulkFilterModal && (
				<ReportTypeViewBulkFilterModal
					accountOptions={accountOptions.users}
					bulkEditTableFilters={bulkEditTableFilters}
					closeModal={onCloseBulkModalFilterClick}
					currentAdditionalFilters={additionalFilters.filter(_filterEmptyAdditionalFilters)}
					currentEndDate={endDate}
					currentPeriod={period}
					currentReportTypeFilter={filterByReportType}
					currentSearch={searchText}
					currentStartDate={startDate}
					equipmentOptions={equipmentOptions}
					fiscalYearStartMonth={companyData.fiscalYearStartMonth}
					jobOptions={jobOptions}
					lazyLoadAccountOptions={lazyLoadAccounts}
					lazyLoadEquipmentOptions={lazyLoadEquipment}
					lazyLoadJobOptions={lazyLoadJobs}
					lazyLoadReportTypes={lazyLoadReportTypes}
					loadReportBlockFieldsForFilter={loadReportBlockFieldsForFilter}
					reportTypeOptions={reportTypeOptions}
					showModal={showBulkFilterModal}

				/>
			)}
			{!!FRTypeForSelectWOsModal && (
				<SelectWorkOrdersModal
					closeModal={closeSelectWOsModal}
					fieldReportTypeId={FRTypeForSelectWOsModal.id}
					jobId={FRTypeForSelectWOsModal.jobId}
					showModal={true}
					workOrderId={FRTypeForSelectWOsModal.workOrderId}
				/>
			)}
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData, userData } = state.user;

	if (!companyData || !userData) {
		throw new Error('User not logged in');
	}

	const isAccountingOrAdmin = isAllowed(
		PagePermissions.COMPANY.FIELD_REPORT.MANAGE.ACCOUNTING,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);
	const isManagement = isAllowed(PagePermissions.COMPANY.FIELD_REPORT.MANAGE.MANAGEMENT, companyData.permissions, companyData.isCompanyAdmin, userData.role);
	const hasPermissionToEdit = isAccountingOrAdmin
		?? isAllowed(PagePermissions.COMPANY.FIELD_REPORT.FILL, companyData.permissions, companyData.isCompanyAdmin, userData.role);
	const hasPermissionToViewAll = isAccountingOrAdmin
		?? isAllowed(PagePermissions.COMPANY.FIELD_REPORT.VIEW_ALL, companyData.permissions, companyData.isCompanyAdmin, userData.role);

	return {
		userData: userData,
		companyData: companyData,
		isAccountingOrAdmin,
		isManagement,
		hasPermissionToEdit,
		hasPermissionToViewAll,
	};
}

function mapDispatchToProps() {
	return {
		loadJobs: JobActions.getAllJobCodesForJobFilter,
		loadReportTypes: ReportTypeActions.getAllForReportTypeFilter,
		loadReportBlockFieldsForFilter: ReportTypeActions.getReportBlockFieldsForFilter,
		findAllForReportTypeViewTable: FieldReportActions.findAllForReportTypeViewTable,
		findFieldReportTypeForSidePanel: FieldReportActions.findFieldReportTypeForSidePanel,
		findOrCreateGroup: FieldReportActions.findOrCreateGroup,
		downloadFieldReportTypePDF: FieldReportActions.downloadFieldReportTypePDF,
		findAllEquipmentForCompany: findAllForOrderReportFilter,
		findAllAccountsForCompany: findAllForReportTypeView,
	};
}

const enhance = compose<React.ComponentClass>(
	connector,
	withTaskRecovery
);

export default enhance(ReportTypeViewTable);
